import React, { FunctionComponent, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';

import { Button, Checkbox, ConfirmDialog } from 'components/UI';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import Appointment, { status } from '@spike/appointment-model';

interface FooterComponentProps {
    appointment?: Appointment;
    withoutButton?: boolean;
    withoutTotal?: boolean;
    success?: boolean;
    title?: string;
    loading: boolean;
    total?: number;
    disabledButton?: boolean;
    hours?: number;
    minutes?: number;
    nights?: string;
    buttonText: string;
    deposit?: string;
    status?: number;
    withTotal?: boolean;
    fastCheckIn?: boolean;
    onBook: () => void;
    /** When set with onSecondaryBook, renders a second outline button (e.g. Skip & Book) beside the primary. */
    secondaryButtonText?: string;
    onSecondaryBook?: () => void;
    onRemove?: () => void;
    onChangeStatus?: (status: number, sendNotification?: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            margin: '16px 36px 13px 36px',
            [theme.breakpoints.up('md')]: {
                margin: '21px 40px 0px 40px'
            }
        },
        totalContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 16,
            [theme.breakpoints.up('md')]: {
                marginBottom: 20
            }
        },
        depositContainer: {
            marginBottom: '14px !important',
            [theme.breakpoints.up('md')]: {
                marginBottom: '16 !important'
            }
        },
        title: {
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '16px'
        },
        normal: {
            fontWeight: 400,
            fontSize: 16,
            lineHeight: '16px'
        },
        button: {
            height: 50,
            [theme.breakpoints.down('sm')]: {
                borderRadius: 30
            },
            [theme.breakpoints.up('md')]: {
                height: 55
            },
            width: '100%'
        },
        buttonDisabled: {
            backgroundColor: '#D3D3D3 !important'
        },
        textContainer: {
            display: 'flex'
        },
        spacing: {
            marginLeft: 8,
            [theme.breakpoints.up('md')]: {
                marginLeft: 10
            }
        },
        buttonContainer: {
            display: 'flex',
            width: '100%',
            justifyContent: 'center'
        },
        dualButtonRow: {
            'flexDirection': 'row',
            'gap': 12,
            '& > *': {
                flex: 1,
                minWidth: 0
            }
        },
        iconRemoveContainer: {
            cursor: 'pointer',
            width: 55,
            minWidth: 55,
            height: 55,
            borderRadius: '50%',
            backgroundColor: '#F1F1F1',
            textAlign: 'center',
            alignContent: 'center',
            marginLeft: 15
        },
        remove: {
            width: '85% !important'
        },
        iconTrash: {
            fontSize: 26
        },
        bookingRequestActionsContainer: {
            'gap': 8,
            'flexDirection': 'row',

            '& button > span': {
                fontSize: '18px !important'
            }
        },
        confirmDialog: {
            '& .MuiDialog-paperWidthSm': {
                maxWidth: 455
            }
        },
        confirmDialogCheckboxContainer: {
            display: 'flex',
            padding: '0px 32px'
        },
        confirmDialogCheckboxText: {
            color: '#7A7A7A',
            textAlign: 'left'
        }
    })
);

export const FooterComponent: FunctionComponent<FooterComponentProps> = props => {
    const classes = useStyles();

    const [showDeclineDialog, setShowDeclineDialog] = useState(false);
    const [sendDeclineNotification, setSendDeclineNotification] = useState(true);

    const getPriceFormatted = (price?: number) => {
        return price ? `${price.toFixed(2)}` : '0.00';
    };

    const declineHandler = () => {
        setShowDeclineDialog(true);
    };

    if (props.status === status.REQUESTED) {
        return (
            <>
                <Box className={clsx(classes.container, classes.bookingRequestActionsContainer)}>
                    <Button
                        label="Decline"
                        variant="danger"
                        loading={props.loading}
                        className={classes.button}
                        onClick={declineHandler}
                    />
                    <Button
                        label="Accept"
                        color="green"
                        loading={props.loading}
                        className={classes.button}
                        onClick={() => props.onChangeStatus?.(status.BOOKED)}
                    />
                </Box>

                <ConfirmDialog
                    open={showDeclineDialog}
                    processing={props.loading}
                    className={classes.confirmDialog}
                    title={<Typography>Decline Appointment</Typography>}
                    question={<Typography>Are you sure you want to decline this appointment?</Typography>}
                    subquestion={
                        <Box className={classes.confirmDialogCheckboxContainer}>
                            <Checkbox
                                checked={sendDeclineNotification}
                                onChange={() => setSendDeclineNotification(!sendDeclineNotification)}
                                label=" "
                            />
                            <Box>
                                <Typography className={classes.confirmDialogCheckboxText}>
                                    Send a notification to {props.appointment?.customer.firstName}{' '}
                                    {props.appointment?.customer.lastName} that their appointment has been decline.
                                </Typography>
                            </Box>
                        </Box>
                    }
                    cancelButtonLabel="Cancel"
                    confirmButtonLabel="Decline"
                    onCancel={() => {
                        setShowDeclineDialog(false);
                        setSendDeclineNotification(true);
                    }}
                    onConfirm={() => {
                        setShowDeclineDialog(false);
                        props.onChangeStatus?.(status.DECLINED, sendDeclineNotification);
                    }}
                />
            </>
        );
    }

    return (
        <Box className={classes.container}>
            {props.deposit && (
                <Box className={clsx(classes.totalContainer, classes.depositContainer)}>
                    <Typography className={classes.normal}>Deposit</Typography>
                    <Box className={classes.textContainer}>
                        <Typography
                            className={classes.normal}
                            id="booking_label_deposit_total"
                        >
                            {getPriceFormatted(Number(props.deposit))}
                        </Typography>
                    </Box>
                </Box>
            )}
            {(props.total || props.deposit || props.withTotal) && (
                <Box className={classes.totalContainer}>
                    <Typography className={classes.title}>{props.title}</Typography>
                    <Box className={classes.textContainer}>
                        <Typography
                            className={classes.title}
                            id="booking_label_total"
                        >
                            {props.total
                                ? `$${getPriceFormatted(props.total)}`
                                : props.withoutTotal
                                ? ''
                                : `$${getPriceFormatted(props.total)}`}
                        </Typography>
                        {(props.hours || props.minutes || props.nights) && !props.deposit ? (
                            props.nights ? (
                                <Typography className={clsx(classes.title, classes.spacing)}>{props.nights}</Typography>
                            ) : (
                                <Typography className={clsx(classes.title, classes.spacing)}>
                                    {`(${props.hours ? props.hours + ' h ' : ''}${
                                        props.minutes ? props.minutes + ' min' : ''
                                    })`}
                                </Typography>
                            )
                        ) : undefined}
                    </Box>
                </Box>
            )}
            <Box
                className={clsx(classes.buttonContainer, {
                    [classes.dualButtonRow]: props.secondaryButtonText && props.onSecondaryBook
                })}
            >
                {!props.withoutButton && props.secondaryButtonText && props.onSecondaryBook && (
                    <Button
                        id="booking_button_skip_secondary"
                        label={props.secondaryButtonText}
                        loading={false}
                        color="black"
                        size="large"
                        variant="secondary"
                        disabled={props.loading}
                        className={classes.button}
                        onClick={props.onSecondaryBook}
                    />
                )}
                {!props.withoutButton && (
                    <Button
                        id={props.success ? 'booking_button_done' : 'booking_button_book'}
                        label={props.buttonText}
                        loading={props.loading}
                        color={props.success ? 'black' : 'green'}
                        size="large"
                        disabled={props.disabledButton && !props.fastCheckIn}
                        variant="primary"
                        className={clsx(classes.button, {
                            [classes.buttonDisabled]: props.disabledButton && !props.fastCheckIn,
                            [classes.remove]: props.onRemove
                        })}
                        onClick={props.onBook}
                    />
                )}
                {props.onRemove && (
                    <Box
                        className={classes.iconRemoveContainer}
                        onClick={props.onRemove}
                    >
                        <FontAwesomeIcon
                            icon={faTrashAlt}
                            className={classes.iconTrash}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default FooterComponent;
